import * as z from "zod";
import type { CountAttributeTasksInput, CountLinesheetInvitationsInput, CountLinesheetsInput } from "../catalog";
import type { CountDocumentsInput } from "../documents";
import { type ListCount, listCountDto, MAX_COUNT_FILTERS } from "../index";
import type { CountErpFilesInput, IntegrationErrorSummary, ReconciliationSummary } from "../integration";
import type {
  CountAvailabilityMismatchesInput,
  CountFulfillmentsInput,
  CountStockAdjustmentsInput,
  CountStockMovementsInput,
  CountVendorReturnsInput,
  CountWaitingAvailabilityInput,
  CountWaitingDifferencesInput,
  CountWaitingMovementExecutionsInput,
  CountWaitingShipmentsInput,
} from "../logistics";
import type {
  CountBcRecordsInput,
  CountEtafApplicationsInput,
  CountHubspotReadingsInput,
  CountHubspotRecordsInput,
  CountOrganizationsInput,
  CountUsersInput,
} from "../party";
import type {
  CountCreditNotesInput,
  CountInboundsInput,
  CountPurchaseOrdersInput,
  CountWaitingReceiptsInput,
} from "../procurement";
import type { CountWaitingGatesInput } from "../reference";
import type { CountJoorOrdersInput, CountSalesOrdersInput, CountShortShipmentsInput } from "../sales";

// The queues of the Backstage home "Now" (flussi-per-ruolo §1) and the counts that serve them (ADR-136): each queue is
// the count of a list of its domain with fixed filters (ADR-086), and the app opens that list with the same filters,
// so the number and the list are one thing. The domains never see these codes.

/** The count operation of each domain list a queue counts, with the filters of its input. */
export interface CountFilters {
  countLinesheets: CountLinesheetsInput;
  countAttributeTasks: CountAttributeTasksInput;
  countLinesheetInvitations: CountLinesheetInvitationsInput;
  countPurchaseOrders: CountPurchaseOrdersInput;
  countInbounds: CountInboundsInput;
  countWaitingReceipts: CountWaitingReceiptsInput;
  countCreditNotes: CountCreditNotesInput;
  countSalesOrders: CountSalesOrdersInput;
  countJoorOrders: CountJoorOrdersInput;
  countShortShipments: CountShortShipmentsInput;
  countDocuments: CountDocumentsInput;
  countEtafApplications: CountEtafApplicationsInput;
  countOrganizations: CountOrganizationsInput;
  countUsers: CountUsersInput;
  countBcRecords: CountBcRecordsInput;
  countHubspotRecords: CountHubspotRecordsInput;
  countHubspotReadings: CountHubspotReadingsInput;
  countStockMovements: CountStockMovementsInput;
  countWaitingMovementExecutions: CountWaitingMovementExecutionsInput;
  countWaitingShipments: CountWaitingShipmentsInput;
  countWaitingAvailability: CountWaitingAvailabilityInput;
  countAvailabilityMismatches: CountAvailabilityMismatchesInput;
  countFulfillments: CountFulfillmentsInput;
  countStockAdjustments: CountStockAdjustmentsInput;
  countWaitingDifferences: CountWaitingDifferencesInput;
  countVendorReturns: CountVendorReturnsInput;
  countWaitingGates: CountWaitingGatesInput;
  countErpFiles: CountErpFilesInput;
}

/**
 * The lists whose domain counts several filters in one call, `<count>ByFilters` (ADR-269): their queues asked
 * together go to the domain together. A list joins when its domain has the method; the others are asked a queue at a
 * time.
 */
export const countsByFilters = [
  "countLinesheets",
  "countAttributeTasks",
  "countPurchaseOrders",
  "countInbounds",
  "countCreditNotes",
] as const satisfies readonly (keyof CountFilters)[];
export type CountByFiltersId = (typeof countsByFilters)[number];

/**
 * The lists whose input takes `anyOf` (ADR-302): the queues of one of them can be counted as a union, the rows in one
 * of them at least, each once. A list joins with the step that gives its domain `anyOf`.
 */
export const countsWithAnyOf = [
  "countLinesheets",
  "countPurchaseOrders",
  "countInbounds",
  "countCreditNotes",
  "countSalesOrders",
  "countOrganizations",
  "countUsers",
  "countStockAdjustments",
  "countFulfillments",
] as const satisfies readonly (keyof CountFilters)[];
export type CountWithAnyOfId = (typeof countsWithAnyOf)[number];

/**
 * Where a queue's number comes from: a domain count with its filters, or a summary of integration (the integration
 * errors, the reconciliation with Business Central).
 */
export type WorkQueueSource =
  | { [K in keyof CountFilters]: { count: K; filters: CountFilters[K] } }[keyof CountFilters]
  | { count: "integrationErrors" }
  | { count: "bcMismatches" };

export const workQueues = {
  // FR-M2: a linesheet the brand submitted waits for Operations; one in draft for the brand
  "to-approve": { count: "countLinesheets", filters: { status: "pending" } },
  // FR-M3-03: an approved linesheet waits for Operations to complete its prices
  "to-price": { count: "countLinesheets", filters: { status: "approved", priceStatus: "pending" } },
  // ADR-060: prices calculated before the Price Setup changed, which the catalog could not recalculate by itself
  "to-recalculate": { count: "countLinesheets", filters: { pricesStale: true } },
  "in-draft": { count: "countLinesheets", filters: { status: "draft" } },
  // spec v0.2 §4, rule 1: whoever works the integrations sees at once that something is stuck
  "integration-errors": { count: "integrationErrors" },
  // FR-M16-03, ADR-177: an entity whose state towards Business Central the hourly check finds wrong
  "bc-mismatches": { count: "bcMismatches" },
  // spec v0.3 §7.5, ADR-258: a file to the ERP waits for who imports it on Business Central
  "erp-files-to-import": { count: "countErpFiles", filters: {} },
  // FR-M4-02, FR-M4-05: the attributes the brand submitted wait for Tomorrow; while a draft, for the brand
  "attributes-to-review": { count: "countAttributeTasks", filters: { attributesStatus: "pending" } },
  "attributes-to-fill": { count: "countAttributeTasks", filters: { attributesStatus: "draft" } },
  // FR-M5-02…07, I-02: a PO waits for Operations to approve, publish, answer or send it again; published, for the
  // supplier to confirm it. The supplier's side (`asSupplier`, ADR-168): a brand reads its POs, but neither confirms
  // nor proposes for a supplier that is not itself; for Tomorrow the filter takes nothing out
  "pos-to-approve": { count: "countPurchaseOrders", filters: { status: "generated" } },
  "pos-to-publish": { count: "countPurchaseOrders", filters: { status: "approved" } },
  "pos-to-decide": { count: "countPurchaseOrders", filters: { pendingChanges: true, asSupplier: true } },
  "pos-sync-failed": { count: "countPurchaseOrders", filters: { bcSyncStatus: "sync_failed" } },
  "pos-to-confirm": { count: "countPurchaseOrders", filters: { status: "published", asSupplier: true } },
  // B-37, ADR-245: a PO the supplier has not confirmed after its last reminder and a cadence, "POs not confirmed"
  "pos-reminders-over": { count: "countPurchaseOrders", filters: { remindersOver: true } },
  // FR-M11-01, ADR-170: a confirmed PO with units no inbound announces yet, for the supplier to upload its documents
  "pos-to-announce": { count: "countPurchaseOrders", filters: { toAnnounce: true, asSupplier: true } },
  // flussi-per-ruolo §3, F-05, v0.2 §9, ADR-133: an order waits for the Customer Service, or with its credit blocked
  // or a payment or a deposit not received, for Credit Control
  "sos-to-allocate": { count: "countSalesOrders", filters: { status: "confirmed" } },
  "sos-to-lock": { count: "countSalesOrders", filters: { lockable: true } },
  "sos-sync-failed": { count: "countSalesOrders", filters: { bcSyncStatus: "sync_failed" } },
  "sos-fulfillment-stopped": { count: "countSalesOrders", filters: { fulfillmentIssue: true } },
  "sos-credit-blocked": { count: "countSalesOrders", filters: { creditBlocked: true } },
  "sos-awaiting-payment": { count: "countSalesOrders", filters: { awaitingPayment: true } },
  "sos-deposit-due": { count: "countSalesOrders", filters: { depositDue: true } },
  // ADR-178 §1.12, ADR-203: a proforma or a deposit not paid after the last reminder waits for Credit Control
  "sos-dunning-over": { count: "countSalesOrders", filters: { dunningOver: true } },
  // ADR-178 §1.13, ADR-187: a locked order a PO lowered by its supplier no longer covers waits for the Customer Service
  "sos-uncovered": { count: "countSalesOrders", filters: { uncovered: true } },
  // C-56, ADR-259: an order the allocation still serves without a season ranks as of no active season
  "sos-without-season": { count: "countSalesOrders", filters: { withoutSeason: true } },
  // B-47, ADR-208: the latest revision of an order confirmation the customer has not answered in its days, or rejected,
  // waits for the Customer Service
  "sos-confirmation-overdue": { count: "countSalesOrders", filters: { confirmation: "overdue" } },
  "sos-confirmation-rejected": { count: "countSalesOrders", filters: { confirmation: "rejected" } },
  // C-45, ADR-261: an order no confirmation was issued or asked for waits for the Customer Service
  "sos-without-confirmation": { count: "countSalesOrders", filters: { withoutConfirmation: true } },
  // ADR-111, B-31, ADR-124: an order of Joor whose latest state is not on a sales order
  "joor-orders-waiting": { count: "countJoorOrders", filters: { waiting: true } },
  // ADR-178 §1.10, ADR-199: a shipment invoiced and closed short by the warehouse waits for Finance's credit note
  "short-shipments-to-credit": { count: "countShortShipments", filters: { credited: false } },
  // FR-M9-07, ADR-118: a document to ask again; one numbered and late with its PDF waits for the Document Service
  "documents-to-retry": { count: "countDocuments", filters: { retryable: true } },
  "documents-pdf-late": { count: "countDocuments", filters: { status: "numbered", overdue: true } },
  // FR-M11-02…07, ADR-128: an inbound waits for Logistics to type and check its packing list; beyond its tolerance,
  // for the brand: the brand's side (ADR-170), since a brand that is not the one of the inbound has nothing to authorize
  "inbounds-to-capture": { count: "countInbounds", filters: { toCapture: true } },
  // B-50, ADR-233: the lines the extraction tool read from a packing list wait for Logistics when it is not sure of one,
  // or when the invoice does not add up
  "inbounds-extraction-to-review": { count: "countInbounds", filters: { extractionToReview: true } },
  "inbounds-to-check": { count: "countInbounds", filters: { status: "lines_captured" } },
  "inbounds-in-exception": { count: "countInbounds", filters: { status: "in_exception" } },
  "inbounds-to-authorize": { count: "countInbounds", filters: { status: "pending_authorization", asBrand: true } },
  // ADR-137: a check passed or forced, the inbound waits for Logistics to send it to the warehouse
  "inbounds-to-dispatch": { count: "countInbounds", filters: { status: "three_way_passed" } },
  // I-11 §5, ADR-138: a receipt of the warehouse Nexxt could not apply waits for Logistics to set right what it lacks
  "receipts-waiting": { count: "countWaitingReceipts", filters: {} },
  // FR-M11-11, ADR-139: an inbound received with units in more or missing waits for Logistics to confirm its outcome
  "inbounds-to-resolve": { count: "countInbounds", filters: { toResolve: true } },
  // ADR-140: units missing that Logistics confirmed wait for the supplier's choice; a credit note for its file, then
  // for Finance. The supplier's side (ADR-170): a brand reads them, but neither chooses nor sends for another supplier
  "inbounds-to-settle": { count: "countInbounds", filters: { toSettle: true, asSupplier: true } },
  "credit-notes-to-send": { count: "countCreditNotes", filters: { toSend: true, asSupplier: true } },
  // B-41, B-42, ADR-245: what the supplier left undone after its last reminder and a cadence: the choice on the missing
  // units and the inbound of the extra units for Logistics, the file of a credit note for Finance
  "inbounds-reminders-over": { count: "countInbounds", filters: { remindersOver: true } },
  "credit-notes-reminders-over": { count: "countCreditNotes", filters: { remindersOver: true } },
  "credit-notes-to-review": { count: "countCreditNotes", filters: { toReview: true, asSupplier: true } },
  // B-44, B-88, ADR-245: an invitation not accepted after its last reminder and a cadence waits for who sent it, Customer
  // Service for a customer, Operations for a brand or a supplier; a brand that is also a customer is in both
  "invitations-reminders-over-customers": { count: "countUsers", filters: { invitationRemindersOver: "customer" } },
  "invitations-reminders-over-partners": {
    count: "countUsers",
    filters: { invitationRemindersOver: "brand_or_supplier" },
  },
  // B-97, ADR-245: a brand still owing its linesheet after the last reminder of the invitation waits for Operations
  "linesheet-invitations-reminders-over": { count: "countLinesheetInvitations", filters: { remindersOver: true } },
  // ADR-105: an e-TAF that was sent or written waits for who reviews it
  "etafs-to-review": { count: "countEtafApplications", filters: { waiting: true } },
  // I-26, ADR-116: a customer or supplier not on Business Central, a record of Business Central to match by hand,
  // one of HubSpot waiting for what HubSpot or the directory lacks
  "organizations-not-on-bc": { count: "countOrganizations", filters: { bcSyncStatus: "sync_failed" } },
  // ADR-093, ADR-189: a supplier without its number on Business Central whose open POs cannot be confirmed
  "suppliers-not-on-bc": { count: "countOrganizations", filters: { toRegisterOnBc: true } },
  // ADR-158, ADR-180: a customer none of whose contacts hears of its orders waits for Customer Service to give the role
  "customers-without-order-contact": { count: "countOrganizations", filters: { withoutOrderContact: true } },
  "bc-records-to-match": { count: "countBcRecords", filters: { waiting: true } },
  "hubspot-records-waiting": { count: "countHubspotRecords", filters: { waiting: true } },
  // ADR-178 §4.4, ADR-191: a full reading of HubSpot that would archive too much, or whose parts did not all arrive,
  // waits for a person before it archives anything
  "hubspot-readings-held": { count: "countHubspotReadings", filters: { waiting: true } },
  // FR-M12-08, ADR-141: an intercompany transfer order Logistics asked for waits for Operations to confirm it
  "transfers-to-confirm": { count: "countStockMovements", filters: { status: "awaiting_confirmation" } },
  // I-11 §5, ADR-142: an execution of the warehouse Nexxt could not apply waits for Logistics to set right what it lacks
  "movement-executions-waiting": { count: "countWaitingMovementExecutions", filters: {} },
  // FR-M12-01, FR-M12-02, ADR-226: an adjustment the IMS proposed waits for Logistics; beyond the threshold, for Finance
  "adjustments-to-confirm": { count: "countStockAdjustments", filters: { status: "draft" } },
  "adjustments-to-approve": { count: "countStockAdjustments", filters: { status: "awaiting_confirmation" } },
  // I-11 §5, ADR-226: a count of the warehouse Nexxt could not make an adjustment of waits for Logistics
  "differences-waiting": { count: "countWaitingDifferences", filters: {} },
  // FR-M12-09, B-54 point 2, ADR-227: a vendor return Logistics opened waits for its supplier to confirm or refuse it,
  // its units held; one the supplier refused waits for Operations
  "vendor-returns-to-confirm": { count: "countVendorReturns", filters: { status: "requested" } },
  "vendor-returns-refused": { count: "countVendorReturns", filters: { status: "awaiting_confirmation" } },
  // I-11 §5, ADR-130: a shipment of the warehouse Nexxt could not apply waits for Logistics to set right what it lacks
  "shipments-waiting": { count: "countWaitingShipments", filters: {} },
  // I-12 §5, ADR-123: a row of the IMS Nexxt could not resolve waits for a mapping or an SKU, then for the IMS
  "availability-waiting": { count: "countWaitingAvailability", filters: {} },
  // B-92, ADR-242: an SKU a full publication of the IMS found different from the events waits for Logistics to check it
  "availability-mismatches": { count: "countAvailabilityMismatches", filters: {} },
  // FR-M13-02, ADR-152: what Tomorrow ships and the warehouse has waits for the Customer Service to book its carrier
  "fulfillments-to-book": { count: "countFulfillments", filters: { toBook: true } },
  // FR-M13-06, ADR-152: a shipment released and not taken by the warehouse in time waits for Logistics to check it
  "fulfillments-without-ack": { count: "countFulfillments", filters: { ackOverdue: true } },
  // B-60, ADR-224: every approval gate open in any domain waits for the role that decides it
  "approvals-waiting": { count: "countWaitingGates", filters: {} },
} as const satisfies Record<string, WorkQueueSource>;

export type WorkQueueId = keyof typeof workQueues;

export const workQueueIds = Object.keys(workQueues) as [WorkQueueId, ...WorkQueueId[]];

/**
 * The queues Atelier may count (ADR-165): its gateway reaches the internet, so worklist asks no domain for a queue only
 * Tomorrow works. A queue enters with the step of Atelier that decides its side, brand or supplier (P6.4b…P6.4d).
 */
export const externalWorkQueueIds = [
  "in-draft",
  "to-approve",
  "attributes-to-fill",
  "attributes-to-review",
  "pos-to-confirm",
  "pos-to-decide",
  "pos-to-announce",
  "inbounds-to-authorize",
  "inbounds-to-settle",
  "credit-notes-to-send",
  "credit-notes-to-review",
  "vendor-returns-to-confirm",
  "vendor-returns-refused",
] as const satisfies readonly WorkQueueId[];
export type ExternalWorkQueueId = (typeof externalWorkQueueIds)[number];

const workQueueId = z.enum(workQueueIds);

const queuesOf = <const Ids extends readonly [WorkQueueId, ...WorkQueueId[]]>(ids: Ids) => {
  const list = z
    .array(z.enum(ids))
    .min(1)
    .max(ids.length)
    .refine((asked) => new Set(asked).size === asked.length, "A queue asked twice");
  return z.object({
    queues: z
      .union([
        list,
        z
          .string()
          // every queue once: a fixed cap fell behind the queues (68 asked for a user with every permission)
          .max(ids.join(",").length)
          .transform((asked) => asked.split(",")),
      ])
      .pipe(list),
  });
};

/** The queues of a list that takes `anyOf`, or none: what a union of queues may join (ADR-302). */
const anyOfListOf = (id: WorkQueueId): CountWithAnyOfId | undefined =>
  countsWithAnyOf.find((list) => list === workQueues[id].count);

const union = z
  .array(workQueueId)
  .min(2)
  .max(MAX_COUNT_FILTERS)
  .refine((queues) => new Set(queues).size === queues.length, "A queue twice in a union")
  .refine((queues) => {
    const list = anyOfListOf(queues[0] as WorkQueueId);
    return list !== undefined && queues.every((id) => anyOfListOf(id) === list);
  }, "A union joins the queues of one list that takes anyOf");

const unions = z.array(union).min(1).max(MAX_COUNT_FILTERS);

/**
 * The queues to count: the ones the app shows, chosen by the user's permissions. In a query string a comma-separated
 * list (`?queues=to-approve,to-price`); via RPC, the array the gateway parsed. `unions`, the queues of a place counted
 * once each row (ADR-302), what the sidebar shows and the filter "To do" of the list finds: in a query string the
 * queues of a union joined by dots, the unions by commas (`?unions=pos-to-approve.pos-to-publish`).
 */
export const countWorkQueuesInput = queuesOf(workQueueIds).extend({
  unions: z
    .union([
      unions,
      z
        .string()
        .max(workQueueIds.join(",").length)
        .transform((asked) => asked.split(",").map((queues) => queues.split("."))),
    ])
    .pipe(unions)
    .optional(),
});
export type CountWorkQueuesInput = z.input<typeof countWorkQueuesInput>;

/** The same, for the external users of Atelier: only the queues of `externalWorkQueueIds` (ADR-165). */
export const countExternalWorkQueuesInput = queuesOf(externalWorkQueueIds);
export type CountExternalWorkQueuesInput = z.input<typeof countExternalWorkQueuesInput>;

/**
 * The count of every queue asked whose domain answered; `failed`, the ones whose domain refused (a queue the Actor
 * cannot read) or did not answer: one domain down does not hide the others. `unions`, the count of each union asked, in
 * its order, null where its domain refused or did not answer (ADR-302).
 */
export const workQueueCountsDto = z
  .object({
    counts: z.partialRecord(workQueueId, listCountDto),
    failed: z.array(workQueueId),
    unions: z.array(listCountDto.nullable()),
  })
  .meta({ id: "WorkQueueCounts" });
export type WorkQueueCounts = z.output<typeof workQueueCountsDto>;

/**
 * The queue of the integration errors (ADR-063): every row with a problem; the oldest is the oldest row that never
 * left, since a dead, failed or overdue row has a time of its own on the page it opens.
 */
export const integrationQueue = ({
  dead,
  failed,
  overdue,
  stalled,
  oldestStalledAt,
}: IntegrationErrorSummary): ListCount => ({ count: dead + failed + overdue + stalled, oldest: oldestStalledAt });

/** The queue of the reconciliation (ADR-177): every open issue. Each has its own time on the page it opens. */
export const reconciliationQueue = ({
  syncCompleteWithoutBcId,
  outcomeNotApplied,
  neverSent,
}: ReconciliationSummary): ListCount => ({
  count: syncCompleteWithoutBcId + outcomeNotApplied + neverSent,
  oldest: null,
});
